import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Ban } from "lucide-react";

export const MOTIVOS_NO_REALIZADA = ["Festivo", "Sin permisos", "Centro cerrado/no disponible", "Falta tiempo", "Acceso", "Otro"];
const DETALLE_OBLIG = new Set(["Sin permisos", "Otro"]);

// Hoja inferior (móvil) para que el montador reporte «No realizada» + motivo rápido. No reprograma ni decide nada más.
export function NoRealizadaSheet({ rutaId, idx, titulo, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [detalle, setDetalle] = useState("");
  const [saving, setSaving] = useState(false);
  const oblig = DETALLE_OBLIG.has(motivo);
  const enviar = async () => {
    if (!motivo) return toast.error("Elige un motivo");
    if (oblig && !detalle.trim()) return toast.error(`Indica el detalle para «${motivo}»`);
    setSaving(true);
    try {
      await api.post(`/rutas/${rutaId}/paradas/${idx}/no-realizada`, { motivo, detalle: detalle.trim() || null });
      toast.success("Parada marcada como NO realizada · oficina decidirá qué hacer");
      onDone?.();
    } catch (e) {
      const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : d?.mensaje || "Error");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center" onClick={() => !saving && onClose()}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="no-realizada-sheet">
        <div className="text-base font-bold text-zinc-100 flex items-center gap-2"><Ban className="w-5 h-5 text-red-400" /> No realizada · {titulo}</div>
        <div className="text-xs text-zinc-400">Indica qué pasó. Oficina decidirá si se replanifica, si hay 2ª visita y quién la paga.</div>
        <div className="grid grid-cols-2 gap-2">
          {MOTIVOS_NO_REALIZADA.map((m) => (
            <button key={m} type="button" onClick={() => setMotivo(m)} data-testid={`nr-motivo-${m.replace(/[^a-z]/gi, "_")}`}
              className={`rounded-lg border px-3 py-3 text-sm font-semibold text-left min-h-[48px] ${motivo === m ? "border-red-500 bg-red-500/20 text-red-100" : "border-zinc-700 bg-zinc-900 text-zinc-200"}`}>{m}</button>
          ))}
        </div>
        <Textarea value={detalle} onChange={(e) => setDetalle(e.target.value)} rows={2} data-testid="nr-detalle"
          placeholder={oblig ? "Detalle obligatorio: quién lo dijo / qué pasó" : "Detalle (opcional)"} className="bg-zinc-900 border-zinc-700 text-sm" />
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} className="flex-1 bg-transparent border-zinc-700">Cancelar</Button>
          <Button onClick={enviar} disabled={saving || !motivo} data-testid="nr-confirmar" className="flex-1 bg-red-500 hover:bg-red-400 text-white font-bold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar no realizada"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Modal al finalizar ruta: obliga a resolver cada parada sin resultado (ir a la parada o marcar no realizada).
export function ResolverParadasModal({ ruta, pendientes, onClose, onAbrirParada, onChange }) {
  const [nr, setNr] = useState(null);
  React.useEffect(() => { if (pendientes.length === 0) onClose(); }, [pendientes.length, onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-zinc-950 border border-amber-500/50 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="resolver-paradas-modal">
        <div className="text-base font-bold text-amber-200">⚠️ {pendientes.length} parada(s) sin resultado</div>
        <div className="text-xs text-zinc-400">No se puede finalizar la ruta hasta indicar qué pasó en cada una: Realizada (reporte) o No realizada (motivo).</div>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {pendientes.map(({ p, idx }) => (
            <div key={idx} className="border border-zinc-800 rounded-lg p-2.5 bg-zinc-900/60" data-testid={`pendiente-${idx}`}>
              <div className="text-sm font-semibold text-zinc-100">#{p.orden} · {p.espacio?.nombre || p.manual_titulo || "Parada"}</div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={() => onAbrirParada(idx)} data-testid={`pendiente-${idx}-realizada`} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-semibold">Realizada · reportar</Button>
                <Button size="sm" onClick={() => setNr({ idx, titulo: p.espacio?.nombre || p.manual_titulo || `#${p.orden}` })} data-testid={`pendiente-${idx}-no-realizada`}
                  className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-500/50">No realizada</Button>
              </div>
            </div>
          ))}
        </div>
        <Button variant="outline" onClick={onClose} className="w-full bg-transparent border-zinc-700">Cerrar</Button>
      </div>
      {nr && <NoRealizadaSheet rutaId={ruta.id} idx={nr.idx} titulo={nr.titulo} onClose={() => setNr(null)} onDone={() => { setNr(null); onChange?.(); }} />}
    </div>
  );
}
